import { useFormikContext } from "formik";
import React, { useEffect } from "react";
import pricingValidation from "../../../../../components/modals/add-new-application-modal/pricing-validation";
import CaretDown24 from "../../../../assets/svg/icons/common/CaretDown24";
import CheckGreenCircleIcon from "../../../../assets/svg/icons/common/check-green-circle";
import FancyCheckBox from "../../../../components/form/fancy-checkbox";
import FancyRadioInput from "../../../../components/form/fancy-radio-input";
import Select from "../../../../components/form/select";
import Switch from "../../../../components/form/switch";
import TextInput from "../../../../components/form/text-input";
import useUserStore from "../../../../stores/user";
import { getPricingValidation } from "../../utils/validation";
import DualPricing from "./dual-pricing";
import FeeDefaults from "./fee-defaults";
import FlatRate from "./flat-rate";
import IcPlus from "./ic-plus";
import styles from "./style.module.scss";
import Surcharge from "./surcharge";
import Tiered from "./tiered";
import baseStyles from "../../style.module.scss";

const Pricing = ({ pricingInfoFields, isOpen, onToggle }) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    useFormikContext();
  const organizationId = useUserStore((state) => state.organizationId);

  // NOTE: Not using isValidSync method here since the yup schema isn't designed to
  // consider the pricing model is selected.
  // TODO: Modify pricingInfoYup schema so that it contains conditional validation
  // based on the selected pricing model.
  const hasErrors = pricingInfoFields.some(
    (field) => Object.hasOwn(errors, field) && Object.hasOwn(touched, field),
  );

  useEffect(() => {
    if (!values.pricing_model) {
      return;
    }

    const validation = getPricingValidation(
      values.pricing_model,
      organizationId,
    );

    if (!validation) {
      return;
    }

    const pinDebitRateKey = `${values.pricing_model}_pin_debit_rate`;
    const pinDebitRateDefault = validation[pinDebitRateKey]?.default;
    if (pinDebitRateDefault !== undefined) {
      setFieldValue("debit_rate", pinDebitRateDefault.toFixed(2));
    }

    const pinDebitPerTxnKey = `${values.pricing_model}_pin_debit_per_transaction`;
    const pinDebitPerTxnDefault = validation[pinDebitPerTxnKey]?.default;
    if (pinDebitPerTxnDefault !== undefined) {
      setFieldValue("debit_per_txn", pinDebitPerTxnDefault.toFixed(2));
    }

    const networkFeeKey = `${values.pricing_model}_pin_debit_network_fee`;
    const networkFeeDefault = validation[networkFeeKey]?.default;
    if (networkFeeDefault !== undefined) {
      const boolValue = String(networkFeeDefault).toLowerCase() === "yes";
      setFieldValue("pin_debit_network_fee", boolValue ? "yes" : "no");
    }
  }, [values.pricing_model, setFieldValue, organizationId]);


  const hasWarning = (value) => {
    if (value > 1) {
      return "Please check pricing.";
    }
    return "";
  };

  return (
    <div className={styles.pricing}>
      <div className={styles.header}>
        <div
          className={`${styles.heading_left} ${baseStyles.headerLeft} ${isOpen && baseStyles.headerLeftOpen}`}
          onClick={onToggle}
        >
          <CaretDown24 />
          <h6 className={styles.title}>Pricing</h6>
        </div>
        <div className={styles.heading_right}>
          {/* <Select
            secondary
            label=""
            placeholder="Apply Template"
            id="apply_template"
            name="apply_template"
            options={[
              { label: "Agent", value: "hands_on" },
              {
                label: "Referral Partner",
                value: "hands_off",
              },
            ]}
            customClass={baseStyles.selectContainer}
            search
            // value={values?.type}
            onChange={(value) => {
              // setFieldValue("type", value);
            }}
          /> */}
          <div className={baseStyles.headerRight}>
            {!hasErrors && <CheckGreenCircleIcon />}
          </div>
        </div>
      </div>

      {isOpen && (
        <div className={`${styles.content_wrapper}`}>
          <div className={styles.content}>
            <p className={styles.p_heading}>Select preferred pricing model</p>

            <div className={styles.pricingButtons}>
              <FancyRadioInput
                id="dual_pricing"
                name="pricing_model"
                label="Dual Pricing"
                value={"dual_pricing"}
                checked={values.pricing_model == "dual_pricing"}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              <FancyRadioInput
                id="surcharge"
                name="pricing_model"
                label="Surcharge"
                value={"surcharge"}
                checked={values.pricing_model == "surcharge"}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              <FancyRadioInput
                id="flat_rate"
                name="pricing_model"
                label="Flat Rate"
                value={"flat_rate"}
                checked={values.pricing_model == "flat_rate"}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              <FancyRadioInput
                id="ic_plus"
                name="pricing_model"
                label="IC+"
                value={"ic_plus"}
                checked={values.pricing_model == "ic_plus"}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              <FancyRadioInput
                id="tiered"
                name="pricing_model"
                label="Tiered Rate"
                value={"tiered"}
                checked={values.pricing_model == "tiered"}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>

            {errors?.pricing_model && touched?.pricing_model && (
              <p className={styles.error}>{errors?.pricing_model}</p>
            )}

            {values.pricing_model && (
              <div className={styles.pricingSection}>
                {values.pricing_model == "dual_pricing" && <DualPricing />}
                {values.pricing_model == "surcharge" && <Surcharge />}
                {values.pricing_model == "flat_rate" && <FlatRate />}
                {values.pricing_model == "ic_plus" && <IcPlus />}
                {values.pricing_model == "tiered" && <Tiered />}
              </div>
            )}

            <div className={styles.card}>
              <h6 className={styles.card_heading}>PIN-BASED DEBIT</h6>
              <div className={styles.card_body}>
                <Switch
                  id="pin_debit_network_fee"
                  name="pin_debit_network_fee"
                  label="Network Fee"
                  checked={values.pin_debit_network_fee === "yes"}
                  onChange={(e) => {
                    handleChange(e);
                    setFieldValue(
                      "pin_debit_network_fee",
                      e.target.checked ? "yes" : "no",
                    );
                  }}
                  onBlur={(e) => {
                    handleBlur(e);
                    setFieldValue(
                      "pin_debit_network_fee",
                      e.target.checked ? "yes" : "no",
                    );
                  }}
                />
                <div
                  className={`${styles.grid} ${styles.gridCols2} ${styles.gap1}`}
                >
                  <TextInput
                    label={"Debit Rate"}
                    placeholder={"0.00"}
                    type="text"
                    suffix={"%"}
                    id="debit_rate"
                    name="debit_rate"
                    value={values?.debit_rate}
                    onBlur={handleBlur}
                    touched={touched?.debit_rate}
                    errored={errors?.debit_rate}
                    error={errors?.debit_rate}
                    cleaveOptions={{
                      numeral: true,
                      numeralDecimalScale: 2,
                      numeralIntegerScale: 4,
                      numeralPositiveOnly: true,
                      onValueChanged: (e) => {
                        handleChange({
                          target: {
                            name: "debit_rate",
                            value: e.target.rawValue,
                          },
                        });
                      },
                    }}
                  />
                  <TextInput
                    label={"Txn Rate"}
                    placeholder={"0.00"}
                    type="text"
                    prefix={"$"}
                    id="debit_per_txn"
                    name="debit_per_txn"
                    value={values?.debit_per_txn}
                    onBlur={handleBlur}
                    touched={touched?.debit_per_txn}
                    errored={errors?.debit_per_txn}
                    error={errors?.debit_per_txn}
                    hasWarning={hasWarning(values?.debit_per_txn)}
                    cleaveOptions={{
                      numeral: true,
                      numeralDecimalScale: 2,
                      numeralIntegerScale: 4,
                      numeralPositiveOnly: true,
                      onValueChanged: (e) => {
                        handleChange({
                          target: {
                            name: "debit_per_txn",
                            value: e.target.rawValue,
                          },
                        });
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            <div className={styles.card}>
              <h6 className={styles.card_heading}>EBT/FNS Transactions</h6>
              <div className={styles.card_body}>
                <Switch
                  id="ebt"
                  name="ebt"
                  label="EBT"
                  checked={values.ebt === "yes"}
                  onChange={(e) => {
                    handleChange(e);
                    setFieldValue("ebt", e.target.checked ? "yes" : "no");
                  }}
                  onBlur={(e) => {
                    handleBlur(e);
                    setFieldValue("ebt", e.target.checked ? "yes" : "no");
                  }}
                />
                {values.ebt === "yes" && (
                  <div
                    className={`${styles.grid} ${styles.gridCols2} ${styles.gap1}`}
                  >
                    <TextInput
                      label={"FNS Number"}
                      placeholder={"FNS Number"}
                      type="text"
                      id="fnf_number"
                      name="fnf_number"
                      value={values?.fnf_number}
                      onBlur={handleBlur}
                      touched={touched?.fnf_number}
                      errored={errors?.fnf_number}
                      error={errors?.fnf_number}
                      disabled={!values.ebt}
                      cleaveOptions={{
                        numericOnly: true,
                        blocks: [7],
                        onValueChanged: (e) => {
                          setFieldValue("fnf_number", e.target.value);
                        },
                      }}
                    />
                    <TextInput
                      label={"EBT Transactions"}
                      placeholder={"0.00"}
                      type="text"
                      prefix={"$"}
                      suffix={"/txn"}
                      id="ebt_transactions"
                      name="ebt_transactions"
                      value={values?.ebt_transactions}
                      onBlur={handleBlur}
                      touched={touched?.ebt_transactions}
                      errored={errors?.ebt_transactions}
                      error={errors?.ebt_transactions}
                      disabled={!values.ebt}
                      cleaveOptions={{
                        numeral: true,
                        numeralDecimalScale: 2,
                        numeralIntegerScale: 4,
                        numeralPositiveOnly: true,
                        onValueChanged: (e) => {
                          handleChange({
                            target: {
                              name: "ebt_transactions",
                              value: e.target.rawValue,
                            },
                          });
                        },
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            <FeeDefaults
              values={values}
              errors={errors}
              handleChange={handleChange}
            />

            <div className={styles.card}>
              <h6 className={styles.card_heading}>VAR sheet</h6>
              <div className={styles.card_body}>
                <Switch
                  id="var_sheet_request"
                  name="var_sheet_request"
                  label="Request VAR sheet only"
                  defaultChecked={values.var_sheet_request}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <p className={styles.p_heading}>
                  Are you already using a gateway or terminal?
                </p>

                <div
                  className={`${styles.flex} ${styles.flexWrap} ${styles.gap1}`}
                >
                  <FancyCheckBox
                    id="Gateway"
                    name="Gateway"
                    label="Gateway"
                    checked={values?.var_only?.type == "gateway"}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFieldValue("var_only", {
                          type: "gateway",
                          model: "",
                        });
                      } else {
                        setFieldValue("var_only", {});
                      }
                    }}
                  />
                  <FancyCheckBox
                    id="Terminal"
                    name="Terminal"
                    label="Terminal"
                    checked={values?.var_only?.type == "terminal"}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFieldValue("var_only", {
                          type: "terminal",
                          model: "",
                          serial: "",
                        });
                      } else {
                        setFieldValue("var_only", {});
                      }
                    }}
                  />
                </div>

                {values?.var_only?.type == "gateway" && (
                  <div
                    className={`${styles.grid} ${styles.gridCols2} ${styles.gap1}`}
                  >
                    <TextInput
                      label={"Gateway name"}
                      placeholder={"Gateway name"}
                      type="text"
                      id="var_only.model"
                      name="var_only.model"
                      value={values?.var_only?.model}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched?.var_only?.model}
                      errored={errors?.var_only?.model}
                      error={errors?.var_only?.model}
                    />
                  </div>
                )}

                {values?.var_only?.type == "terminal" && (
                  <div
                    className={`${styles.grid} ${styles.gridCols2} ${styles.gap1}`}
                  >
                    <TextInput
                      label={"Model"}
                      placeholder={"Model"}
                      type="text"
                      id="var_only.model"
                      name="var_only.model"
                      value={values?.var_only?.model}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched?.var_only?.model}
                      errored={errors?.var_only?.model}
                      error={errors?.var_only?.model}
                    />

                    <TextInput
                      label={"Serial Number"}
                      placeholder={"Serial Number"}
                      type="text"
                      id="var_only.serial"
                      name="var_only.serial"
                      value={values?.var_only?.serial}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched?.var_only?.serial}
                      errored={errors?.var_only?.serial}
                      error={errors?.var_only?.serial}
                    />
                  </div>
                )}

                {
                  // TODO: Add this back in when we have the support for multiple
                  // VAR hardware components to reprogram
                }
                {/*
                  <FieldArray name="reprogrammed_hardware">
                    {({ push, remove }) => (
                      <>
                        {values.reprogrammed_hardware?.map((obj, index) => (
                          <ReprogramHardware
                            key={index}
                            id={index}
                            data={obj}
                            errors={errors?.reprogrammed_hardware || []}
                            removeItem={() => remove(index)}
                            onChange={(i, e) => {
                              setFieldValue(
                                `reprogrammed_hardware[${i}].${e.target.name}`,
                                e.target.value,
                              );
                            }}
                          />
                        ))} <div>
                          <ButtonIcon
                            textColor="#254FE2"
                            onClick={() =>
                              push(reprogrammedHardwareInitialValues)
                            }
                            type="button"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M14 8C14 8.13261 13.9473 8.25979 13.8536 8.35355C13.7598 8.44732 13.6326 8.5 13.5 8.5H8.5V13.5C8.5 13.6326 8.44732 13.7598 8.35355 13.8536C8.25979 13.9473 8.13261 14 8 14C7.86739 14 7.74021 13.9473 7.64645 13.8536C7.55268 13.7598 7.5 13.6326 7.5 13.5V8.5H2.5C2.36739 8.5 2.24021 8.44732 2.14645 8.35355C2.05268 8.25979 2 8.13261 2 8C2 7.86739 2.05268 7.74021 2.14645 7.64645C2.24021 7.55268 2.36739 7.5 2.5 7.5H7.5V2.5C7.5 2.36739 7.55268 2.24021 7.64645 2.14645C7.74021 2.05268 7.86739 2 8 2C8.13261 2 8.25979 2.05268 8.35355 2.14645C8.44732 2.24021 8.5 2.36739 8.5 2.5V7.5H13.5C13.6326 7.5 13.7598 7.55268 13.8536 7.64645C13.9473 7.74021 14 7.86739 14 8Z"
                                fill="#254FE2"
                              />
                            </svg>
                            <span>Add Terminal</span>
                          </ButtonIcon>
                        </div> 
                      </>
                    )}
                  </FieldArray>
                   */}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;
